import type { Ref } from 'vue';
import type { PatternCell, PatternCoordinates } from '~/utils/auth-pattern';
import { useMediaQuery } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { cellKey, findPath, getCell, LAST_ENTRY_KEY, reachableCells, START_CELL } from '~/utils/auth-pattern';

export const WALK_TIMING = {
	initialDelay: 1600,
	idleStep: 2550,
	pointerStep: 1000,
	keyboardStep: 80,
	settleAfterLeave: 1500,
	travelStep: 120,
	resumeDelay: 80,
} as const;

/** Keeps an unattended wander visibly long, rather than nudging the highlight to a neighbour. */
const MIN_WANDER_DISTANCE = 3;

const ARROW_STEPS: Record<string, PatternCoordinates> = {
	ArrowUp: { row: -1, column: 0 },
	ArrowDown: { row: 1, column: 0 },
	ArrowLeft: { row: 0, column: -1 },
	ArrowRight: { row: 0, column: 1 },
};

export function useAuthPatternWalk(svg: Ref<SVGSVGElement | null>) {
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

	const current = ref<PatternCoordinates>({ ...START_CELL });
	const target = ref<PatternCoordinates>({ ...START_CELL });
	const isEntering = ref(true);
	const highlightedKey = computed(() => cellKey(current.value.row, current.value.column));

	/** A pointer or the keyboard is driving the highlight, so the unattended wander stays off. */
	let isAttended = false;
	/** A click asked the highlight to come over, so it steps at the travel cadence until it lands. */
	let isTravelling = false;
	let moveTimer: ReturnType<typeof setTimeout> | undefined;

	function clearMoveTimer() {
		if (moveTimer === undefined) return;
		clearTimeout(moveTimer);
		moveTimer = undefined;
	}

	function stepDelay(): number {
		if (isTravelling) return WALK_TIMING.travelStep;
		return isAttended ? WALK_TIMING.pointerStep : WALK_TIMING.idleStep;
	}

	function schedule(delay = stepDelay()) {
		clearMoveTimer();
		if (reducedMotion.value) return;
		moveTimer = setTimeout(tick, delay);
	}

	function isAtTarget(): boolean {
		return current.value.row === target.value.row && current.value.column === target.value.column;
	}

	function setTarget(coordinates: PatternCoordinates, travelling = false) {
		target.value = { row: coordinates.row, column: coordinates.column };
		isTravelling = travelling;
	}

	function setRandomTarget() {
		const possibilities = reachableCells.filter((cell) => {
			const distance = Math.abs(cell.row - current.value.row) + Math.abs(cell.column - current.value.column);
			return distance >= MIN_WANDER_DISTANCE;
		});

		const next = possibilities[Math.floor(Math.random() * possibilities.length)];
		if (!next) return;
		setTarget(next);
	}

	function advanceOneCell(): boolean {
		const [next] = findPath(current.value, target.value);
		if (!next) return false;
		current.value = next;
		return true;
	}

	function tick() {
		if (!advanceOneCell() && !isAttended) {
			setRandomTarget();
			advanceOneCell();
		}

		if (isAtTarget()) isTravelling = false;

		schedule();
	}

	function nearestCell(event: PointerEvent): PatternCell | undefined {
		const element = svg.value;
		const matrix = element?.getScreenCTM();
		if (!element || !matrix) return;

		const point = element.createSVGPoint();
		point.x = event.clientX;
		point.y = event.clientY;
		const local = point.matrixTransform(matrix.inverse());

		return reachableCells.reduce<{ cell?: PatternCell; distance: number }>((nearest, cell) => {
			const distance = Math.hypot(cell.x - local.x, cell.y - local.y);
			return distance < nearest.distance ? { cell, distance } : nearest;
		}, { distance: Number.POSITIVE_INFINITY }).cell;
	}

	function followPointer(event: PointerEvent) {
		const nearest = nearestCell(event);
		if (!nearest) return;
		setTarget(nearest);

		if (reducedMotion.value) current.value = { ...target.value };
	}

	function handlePointerEnter(event: PointerEvent) {
		isAttended = true;
		followPointer(event);
		schedule();
	}

	function handlePointerLeave() {
		isAttended = false;
		setRandomTarget();
		schedule(WALK_TIMING.settleAfterLeave);
	}

	function selectCell(cell: PatternCell) {
		if (!cell.reachable) return;

		// Glide to the clicked cell so the click reads as a destination rather than a teleport.
		setTarget(cell, true);

		if (reducedMotion.value) current.value = { ...target.value };
		else schedule();
	}

	function handleKeydown(event: KeyboardEvent) {
		const step = ARROW_STEPS[event.key];
		if (!step) return;

		const next = getCell(target.value.row + step.row, target.value.column + step.column);
		if (!next) return;

		event.preventDefault();
		isAttended = true;
		setTarget(next);

		if (reducedMotion.value) current.value = { ...target.value };
		else schedule(WALK_TIMING.keyboardStep);
	}

	function handleBlur() {
		isAttended = false;
		setRandomTarget();
		schedule(WALK_TIMING.settleAfterLeave);
	}

	function handleEntryAnimationEnd(cell: PatternCell) {
		if (cell.key === LAST_ENTRY_KEY) isEntering.value = false;
	}

	watch(reducedMotion, (shouldReduceMotion) => {
		if (shouldReduceMotion) {
			isEntering.value = false;
			clearMoveTimer();
			return;
		}

		setRandomTarget();
		schedule(WALK_TIMING.resumeDelay);
	});

	onMounted(() => {
		if (reducedMotion.value) isEntering.value = false;
		setRandomTarget();
		schedule(WALK_TIMING.initialDelay);
	});

	onBeforeUnmount(clearMoveTimer);

	return {
		highlightedKey,
		isEntering,
		followPointer,
		handlePointerEnter,
		handlePointerLeave,
		handleKeydown,
		handleBlur,
		selectCell,
		handleEntryAnimationEnd,
	};
}
